import { useState } from 'react';
import clsx from 'clsx';
import greenTick from '../../assets/green-tick.svg';
import CaretIcon from '../../assets/Svg/CaretIcon';
import CrossFilledIcon from '../../assets/Svg/crossFilledIcon';
import MinusFilledIcon from '../../assets/Svg/MinusFilledIcon';
import Tooltip from '../_custom/Tooltip/Tooltip';
import { COLORS } from '../../utils/constants/colors';
import {
    HIDDEN_MATCH_SIGNAL_TYPE,
    MATCH_NOT_EVALUATED_LABEL,
    MATCH_NOT_EVALUATED_LEGACY_REASON,
    MATCH_NOT_EVALUATED_PENDING_REASON,
    MATCH_ROWS_COLLAPSED,
    MATCH_SHOW_LESS,
    MATCH_SHOW_MORE,
    MATCH_STRENGTH_LABEL,
    MATCH_STATUS_TEXT,
    MATCH_TABLE_HEADERS,
    collapseCompanyNameRows,
    mergeDirectorRows,
    formatMatchSignalLabel,
    formatMatchSignalValue,
    isSignalShown,
    matchSignalsComparedLabel,
    matchTheirsHeader,
} from './constants';
import styles from './MatchStrength.module.scss';
import type { MatchEvidence, MatchEvidenceAbsentReason, MatchStatus } from './types';

export interface MatchStrengthProps {
    matchEvidence?: MatchEvidence;
    matchEvidenceAbsentReason?: MatchEvidenceAbsentReason;
}

const VERDICT_META: Record<MatchEvidence['verdict'], { label: string; className: string }> = {
    CONFIRMED: { label: 'Confirmed', className: styles.Band_Confirmed },
    PROBABLE: { label: 'Probable', className: styles.Band_Probable },
};

/** Icon and row tint for a compared signal's match status (its text lives in MATCH_STATUS_TEXT);
 *  also builds the legend, in this order. */
const MATCH_STATUS_META: Record<MatchStatus, { icon: JSX.Element; rowClass: string }> = {
    EXACT: { icon: <img src={greenTick} alt="" className={styles.MarkIcon} />, rowClass: styles.Row_Exact },
    PARTIAL: { icon: <MinusFilledIcon className={styles.MarkIcon} />, rowClass: styles.Row_Partial },
    MISMATCH: { icon: <CrossFilledIcon colour={COLORS.RED} className={styles.MarkIcon} />, rowClass: styles.Row_Mismatch },
};

const MATCH_STATUSES = Object.keys(MATCH_STATUS_META) as MatchStatus[];

/** A status icon. `decorative` marks it aria-hidden where visible text beside it already names the
 *  status (the legend), so a screen reader doesn't read the status twice. */
const Mark = ({ status, decorative = false }: { status: MatchStatus | null; decorative?: boolean }) => {
    if (!status) return null;
    const { icon } = MATCH_STATUS_META[status];
    if (decorative) {
        return (
            <span className={styles.Mark} aria-hidden="true">
                {icon}
            </span>
        );
    }
    const { legendLabel } = MATCH_STATUS_TEXT[status];
    return (
        <span className={styles.Mark} role="img" aria-label={legendLabel} title={legendLabel}>
            {icon}
        </span>
    );
};

/**
 * The evidence behind a finding's confidence, or a neutral acknowledgement when the engine never
 * scored it. `strength` never renders anywhere -- in text or in any attribute -- and neither does
 * a signal's `score` in this card's own table; a regulatory hit's winning name chip does render
 * its score, rounded to a percent (see MatchSignalChips.tsx).
 */
const MatchStrength = ({ matchEvidence, matchEvidenceAbsentReason }: MatchStrengthProps) => {
    const [expanded, setExpanded] = useState(false);

    if (!matchEvidence) {
        return (
            <section className={styles.Card} aria-label={MATCH_STRENGTH_LABEL}>
                <div className={styles.HeaderTitle}>
                    <span className={styles.Label}>{MATCH_STRENGTH_LABEL}</span>
                    <span className={clsx(styles.Band, styles.Band_NotEvaluated)}>{MATCH_NOT_EVALUATED_LABEL}</span>
                </div>
                <p className={clsx(styles.NotEvaluatedReason, styles.Muted)}>
                    {matchEvidenceAbsentReason ? MATCH_NOT_EVALUATED_PENDING_REASON : MATCH_NOT_EVALUATED_LEGACY_REASON}
                </p>
            </section>
        );
    }

    const { verdict, signals } = matchEvidence;
    // The engine's own signalsCompared includes every hidden signal (HIDDEN_MATCH_SIGNAL_TYPE, and
    // per-domain ones -- see isSignalShown), so the header count is derived from the rows actually
    // rendered instead -- it describes the table in front of the reader, and a hidden compared row
    // must not leave the two disagreeing.
    const comparedSignals = signals.filter(
        (signal) => signal.compared && signal.type !== HIDDEN_MATCH_SIGNAL_TYPE && isSignalShown(signal, matchEvidence.domain),
    );
    const rows = mergeDirectorRows(collapseCompanyNameRows(comparedSignals));
    const hasRows = rows.length > 0;
    const visible = expanded ? rows : rows.slice(0, MATCH_ROWS_COLLAPSED);

    return (
        <section className={styles.Card} aria-label={MATCH_STRENGTH_LABEL}>
            <header className={styles.Header}>
                <div className={styles.HeaderTitle}>
                    <span className={styles.Label}>{MATCH_STRENGTH_LABEL}</span>
                    <span className={clsx(styles.Band, VERDICT_META[verdict].className)}>{VERDICT_META[verdict].label}</span>
                    {hasRows && <span className={styles.Muted}>{matchSignalsComparedLabel(rows.length)}</span>}
                </div>
                {hasRows && (
                    <ul className={styles.Legend}>
                        {MATCH_STATUSES.map((status) => (
                            <li className={clsx(styles.LegendItem, styles.Muted)} key={status}>
                                <Mark status={status} decorative />
                                <span>{MATCH_STATUS_TEXT[status].legendLabel}</span>
                            </li>
                        ))}
                    </ul>
                )}
            </header>

            {hasRows && (
                <div className={styles.TableWrap}>
                    <table className={styles.Table}>
                        <thead>
                            <tr>
                                <th scope="col" className={styles.IconCell}>
                                    <span className={styles.SrOnly}>{MATCH_TABLE_HEADERS.STATUS}</span>
                                </th>
                                <th scope="col" className={styles.SignalCell}>
                                    {MATCH_TABLE_HEADERS.SIGNAL}
                                </th>
                                <th scope="col" className={styles.ColDivider}>
                                    {MATCH_TABLE_HEADERS.OURS}
                                </th>
                                <th scope="col">{matchTheirsHeader(matchEvidence.domain)}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {visible.map((signal, index) => {
                                const ours = formatMatchSignalValue(signal.type, signal.ours);
                                const theirs = formatMatchSignalValue(signal.type, signal.theirs);
                                return (
                                    <tr
                                        className={clsx(styles.Row, signal.status && MATCH_STATUS_META[signal.status].rowClass)}
                                        key={`${signal.type}-${index}`}
                                    >
                                        <td className={styles.IconCell}>
                                            <Mark status={signal.status} />
                                        </td>
                                        <th scope="row" className={styles.SignalCell} title={signal.note || undefined}>
                                            {formatMatchSignalLabel(signal.type, signal.label)}
                                        </th>
                                        <td className={clsx(styles.ValueCell, styles.ColDivider)}>
                                            <Tooltip content={ours} direction="top" usePortal containerFullWidth fitContent>
                                                <div className={styles.ValueCellText}>{ours}</div>
                                            </Tooltip>
                                        </td>
                                        <td className={styles.ValueCell}>
                                            <Tooltip content={theirs} direction="top" usePortal containerFullWidth fitContent>
                                                <div className={styles.ValueCellText}>{theirs}</div>
                                            </Tooltip>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {rows.length > MATCH_ROWS_COLLAPSED && (
                <button
                    type="button"
                    className={styles.ShowMore}
                    aria-expanded={expanded}
                    onClick={() => setExpanded((previous) => !previous)}
                >
                    {expanded ? MATCH_SHOW_LESS : MATCH_SHOW_MORE}
                    <span aria-hidden="true" className={clsx(styles.ShowMoreChevron, { [styles.ShowMoreChevron_Expanded]: expanded })}>
                        <CaretIcon colour={COLORS.PRIMARY_PURPLE} width="12" height="8" />
                    </span>
                </button>
            )}
        </section>
    );
};

export default MatchStrength;
